import React, { useEffect, useRef, useState } from 'react';
import { loadAdminUsageEvents } from '../utils/adminActivityClient';
import usageContract from '../../functions/usageContract.js';
import { normalizeActivity } from '../utils/activityEvents';

const labels = Object.fromEntries(usageContract.ANALYTICS_TYPES.map(type => [type, normalizeActivity({ type }).summary]));
const time = milliseconds => new Date(milliseconds).toLocaleString();

export default function UsageAnalytics({ wallet }) {
    const uid = wallet.publicKey?.toBase58?.() || '';
    if (!uid || !wallet.connected) return <p className="admin-panel" role="status">Connect your wallet to view usage analytics.</p>;
    return <UsageAnalyticsPanel key={uid + ':' + !!wallet.connected} wallet={wallet} uid={uid} />;
}
function UsageAnalyticsPanel({ wallet, uid }) {
    const walletRef = useRef(wallet);
    walletRef.current = wallet;
    const requestRef = useRef(0);
    const [days, setDays] = useState(7);
    const [type, setType] = useState('all');
    const [pages, setPages] = useState([null]);
    const [refresh, setRefresh] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const cursor = pages[pages.length - 1];

    useEffect(() => {
        const request = ++requestRef.current;
        const isCurrent = () => requestRef.current === request && walletRef.current.connected && walletRef.current.publicKey?.toBase58?.() === uid;
        setLoading(true);
        setError('');
        loadAdminUsageEvents(walletRef.current, { days, type, cursor, includeSummary: !cursor }, { isCurrent }).then(result => {
            if (isCurrent()) setData(previous => ({ ...previous, ...result }));
        }).catch(failure => {
            if (isCurrent()) setError(failure.message || 'Usage analytics could not be loaded.');
        }).finally(() => { if (isCurrent()) setLoading(false); });
        return () => { requestRef.current += 1; };
    }, [uid, days, type, cursor, refresh]);

    return <section className="admin-panel" aria-label="Usage analytics" style={{ padding: 16, marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Homepage and login analytics</h2>
        <p>Browser reports measure visits and attempts. Completed wallet logins require a verified signed session. Browser sessions approximate visitors.</p>
        <div className="notification-inbox-toolbar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label>Period <select aria-label="Usage period" value={days} onChange={event => { setDays(Number(event.target.value)); setPages([null]); }}>
                {[7, 30, 90].map(value => <option key={value} value={value}>Last {value} UTC days</option>)}
            </select></label>
            <label>Event <select aria-label="Usage event" value={type} onChange={event => { setType(event.target.value); setPages([null]); }}>
                <option value="all">All usage events</option>
                {Object.entries(labels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select></label>
            <button type="button" className="muulti-button" style={{ width: 'auto' }} disabled={loading}
                onClick={() => { setPages([null]); setRefresh(value => value + 1); }}>Refresh usage</button>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading && <p role="status">Loading usage analytics...</p>}
        {data && !loading && !error && <>
            <p>{new Date(data.from).toISOString().slice(0, 10)} through {new Date(data.through).toISOString().slice(0, 10)} (UTC).
                {' '}<b>{data.observedSessions}</b> observed browser sessions. Totals cover all event types in this period.</p>
            <div className="admin-analytics-counts" style={{ flexWrap: 'wrap' }}>
                {Object.entries(labels).map(([key, label]) => <div key={key}><b>{data.counts[key] || 0}</b><span>{label}</span></div>)}
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table className="admin-table" style={{ width: '100%', textAlign: 'left' }}>
                    <caption>Usage events, newest first</caption>
                    <thead><tr>{['Time', 'Event', 'Evidence', 'Flow', 'Failure', 'Wallet / session'].map(label => <th key={label}>{label}</th>)}</tr></thead>
                    <tbody>{data.items.map(item => <tr key={item.id}>
                        <td>{time(item.createdAt)}</td><td>{labels[item.type] || item.summary}</td>
                        <td>{item.evidence === 'server_verified' ? 'Server verified' : 'Browser reported'}</td>
                        <td>{item.flow || '—'}</td><td>{item.failureCode || '—'}</td>
                        <td style={{ overflowWrap: 'anywhere' }}>{item.actorUid || (item.sessionId ? 'Session ' + item.sessionId.slice(0, 8) : 'Anonymous')}</td>
                    </tr>)}</tbody>
                </table>
            </div>
            {!data.items.length && <p>No usage events match this period and filter.</p>}
        </>}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <button type="button" className="muulti-button" style={{ width: 'auto' }} disabled={loading || pages.length === 1}
                onClick={() => setPages(previous => previous.slice(0, -1))}>Previous usage page</button>
            <span>Page {pages.length}</span>
            <button type="button" className="muulti-button" style={{ width: 'auto' }} disabled={loading || !!error || !data?.cursor}
                onClick={() => setPages(previous => [...previous, data.cursor])}>Next usage page</button>
        </div>
    </section>;
}
